import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalContent,
  ModalOverlay,
  PortalManager,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React from "react";
import HideLegacyFooter from "../../shared/components/HideLegacyFooter";
import { FiAlertTriangle, FiFileText } from "react-icons/fi";
import useApi from "../../shared/hooks/useApi";
import { PatientDocumentTypeId, PatientTaskInstanceId } from "../../shared/schema/schema";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import DocumentReviewContent from "./DocumentReviewContent";
import ReviewHeader from "./ReviewHeader";

export type { DocumentReview } from "./DocumentReviewContent";

// Same layer as the Tasks 2.0 drawer: under the AngularJS Bootstrap modal (backdrop 1040,
// dialog 1050) so an AngularJS modal opened meanwhile still shows on top, and rendered after the
// drawer (inside its content, see TasksNewTaskDrawer) so it covers it.
const REVIEW_MODAL_Z_INDEX = 1039;
// Chakra popovers/menus rendered in a <Portal> inside the review land in a container at this
// z-index (their own z-index is tiny), so they show above the review; the AngularJS modal, added
// to the body later, still wins the tie. Tooltips (1800) and the DatePicker popup (20000, its own
// portal) are above regardless.
const REVIEW_PORTAL_Z_INDEX = REVIEW_MODAL_Z_INDEX + 1;
const SIDE_PANEL_WIDTH = "380px";

interface Props {
  documentTypeId: PatientDocumentTypeId;
  isOpen: boolean;
  taskInstanceId: PatientTaskInstanceId;
  onClose: () => void;
}

/** Full-screen review of one task document: PDF-like preview on the left, side panel right. */
export default function DocumentReviewModal(props: Props) {
  // Escape and the close button go through the loaded review first: it may ask before discarding
  // unsent comments, and to submit edits of a submitted document (see DocumentReviewContent).
  const closeRequestRef = React.useRef<(() => void) | null>(null);
  const handleClose = () => {
    if (closeRequestRef.current !== null) {
      closeRequestRef.current();
    } else {
      props.onClose();
    }
  };

  // With trapFocus off Chakra does not move focus into the modal, so Escape (handled on the
  // dialog) would do nothing until something inside was clicked: focus the dialog when it mounts.
  const focusOnMount = React.useCallback((element: HTMLElement | null) => {
    element?.focus({ preventScroll: true });
  }, []);

  return (
    <Modal isOpen={props.isOpen} size="full" trapFocus={false} onClose={handleClose}>
      {props.isOpen && <HideLegacyFooter />}
      <ModalOverlay zIndex={REVIEW_MODAL_Z_INDEX} />
      <ModalContent
        ref={focusOnMount}
        bg="gray.100"
        borderRadius={0}
        containerProps={{ zIndex: REVIEW_MODAL_Z_INDEX }}
        display="flex"
        flexDirection="column"
        h="100vh"
        m={0}
        overflow="hidden"
      >
        {props.isOpen && (
          <PortalManager zIndex={REVIEW_PORTAL_Z_INDEX}>
            <DocumentReviewLoader
              closeRequestRef={closeRequestRef}
              documentTypeId={props.documentTypeId}
              taskInstanceId={props.taskInstanceId}
              onClose={props.onClose}
            />
          </PortalManager>
        )}
      </ModalContent>
    </Modal>
  );
}

function isNotFound(error: Error): boolean {
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  return status === 403 || status === 404;
}

function DocumentReviewLoader(props: {
  closeRequestRef: React.MutableRefObject<(() => void) | null>;
  documentTypeId: PatientDocumentTypeId;
  taskInstanceId: PatientTaskInstanceId;
  onClose: () => void;
}) {
  const { queries } = useApi();
  const reviewQuery = useQuery(
    queries.documentReview.review(props.taskInstanceId, props.documentTypeId)
  );

  // Data first: once loaded, the review stays mounted even when a background refetch fails (React
  // Query then reports status "error" but keeps the data). Unmounting it would lose the session's
  // drafts, the edits still to submit, and the close guard.
  if (reviewQuery.data !== undefined) {
    return (
      <DocumentReviewContent
        closeRequestRef={props.closeRequestRef}
        documentTypeId={props.documentTypeId}
        isRefreshing={reviewQuery.isFetching}
        refreshError={reviewQuery.isRefetchError ? reviewQuery.error : null}
        review={reviewQuery.data}
        taskInstanceId={props.taskInstanceId}
        onClose={props.onClose}
        onRefresh={() => reviewQuery.refetch()}
      />
    );
  }

  switch (reviewQuery.status) {
    case "pending":
      return (
        <>
          <ReviewHeader>
            <Skeleton borderRadius="md" h={6} w="320px" />
          </ReviewHeader>
          <Flex flex={1} minH={0}>
            <Box flex={1} overflow="hidden" px={6} py={8}>
              <Box bg="white" boxShadow="sm" maxW="100%" mx="auto" px="48px" py="32px" w="816px">
                <Skeleton h={4} w="40%" />
                <Skeleton h={5} mt={8} mx="auto" w="50%" />
                <Skeleton borderRadius="lg" h={12} mt={6} />
                <Stack mt={8} spacing={6}>
                  {Array.from({ length: 6 }, (_, index) => (
                    <Skeleton key={index} h={10} />
                  ))}
                </Stack>
              </Box>
            </Box>
            <Box
              bg="gray.50"
              borderColor="gray.200"
              borderLeftWidth={1}
              flexShrink={0}
              p={5}
              w={SIDE_PANEL_WIDTH}
            >
              <Skeleton borderRadius="full" h={5} w="96px" />
              <Skeleton h={4} mt={3} w="60%" />
              <Stack mt={8} spacing={3}>
                <Skeleton borderRadius="md" h="72px" />
                <Skeleton borderRadius="md" h="72px" />
              </Stack>
            </Box>
          </Flex>
        </>
      );
    case "error": {
      const notFound = isNotFound(reviewQuery.error);
      return (
        <>
          <ReviewHeader>
            <Heading fontWeight="semibold" size="md">
              Document review
            </Heading>
          </ReviewHeader>
          <Flex align="center" direction="column" flex={1} gap={3} justify="center" px={6}>
            <Icon
              as={notFound ? FiFileText : FiAlertTriangle}
              boxSize={8}
              color={notFound ? "gray.300" : "red.400"}
            />
            <Text color="gray.700" fontWeight="medium">
              {notFound
                ? "This document could not be found."
                : "Failed to load the document for review."}
            </Text>
            {!notFound && (
              <>
                <Text color="gray.500" fontSize="sm" maxW="lg" textAlign="center">
                  {formatErrorResponse(reviewQuery.error)}
                </Text>
                <Button
                  isLoading={reviewQuery.isFetching}
                  size="sm"
                  onClick={() => reviewQuery.refetch()}
                >
                  Retry
                </Button>
              </>
            )}
          </Flex>
        </>
      );
    }
  }
}
